// API service for Survey Shield

import axios from 'axios';
import {
  SurveyAnalysisRequest,
  SurveyAnalysisResult,
  SurveyConfig,
  ApiResponse
} from '../types/survey';
import {
  InstrumentReview,
  InstrumentReviewStatus,
  InstrumentReviewSubmitResponse
} from '../types/instrument';

const API_BASE_URL = process.env.REACT_APP_API_URL || (
  process.env.NODE_ENV === 'production'
    ? window.location.origin
    : 'http://localhost:8000'
);

const api = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for debugging
api.interceptors.request.use(
  (config) => {
    console.log('API Request:', config.method?.toUpperCase(), config.url);
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.error('API Response Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

export const surveyApi = {
  submitAnalysis: async (request: SurveyAnalysisRequest): Promise<ApiResponse<{ analysis_id: string; status: string; message: string; estimated_time: string }>> => {
    try {
      const response = await api.post('/survey/analyze', request);
      return { data: response.data };
    } catch (error: any) {
      return { error: error.response?.data?.detail || error.message };
    }
  },

  getResults: async (analysisId: string): Promise<ApiResponse<SurveyAnalysisResult>> => {
    try {
      const response = await api.get(`/survey/results/${analysisId}`);
      return { data: response.data };
    } catch (error: any) {
      return { error: error.response?.data?.detail || error.message };
    }
  },

  getConfig: async (): Promise<ApiResponse<SurveyConfig>> => {
    try {
      const response = await api.get('/survey/config');
      return { data: response.data };
    } catch (error: any) {
      return { error: error.response?.data?.detail || error.message };
    }
  },

  reportUrl: (analysisId: string): string =>
    `${API_BASE_URL}/api/v1/survey/report/${analysisId}`,
};

export const instrumentApi = {
  submitReview: async (
    file: File,
    modelName: string,
  ): Promise<ApiResponse<InstrumentReviewSubmitResponse>> => {
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('model_name', modelName);
      const response = await api.post('/instrument/review', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return { data: response.data };
    } catch (error: any) {
      return { error: error.response?.data?.detail || error.message };
    }
  },

  getStatus: async (
    reviewId: string,
  ): Promise<ApiResponse<InstrumentReviewStatus>> => {
    try {
      const response = await api.get(`/instrument/status/${reviewId}`);
      return { data: response.data };
    } catch (error: any) {
      return { error: error.response?.data?.detail || error.message };
    }
  },

  getResults: async (
    reviewId: string,
  ): Promise<ApiResponse<InstrumentReview>> => {
    try {
      const response = await api.get(`/instrument/results/${reviewId}`);
      return { data: response.data };
    } catch (error: any) {
      return { error: error.response?.data?.detail || error.message };
    }
  },

  reportUrl: (reviewId: string): string =>
    `${API_BASE_URL}/api/v1/instrument/report/${reviewId}`,
};

export default surveyApi;